// Drawing / document control and the standards & specification library —
// same controlled lifecycle, so one page serves both (`kind` prop).
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { D, designError, fmtDate, label, useDesignMe } from "@/lib/designApi";
import {
  DataTable, ErrorList, Field, FilesPanel, HistoryPanel, ItemPicker, NoAccess, ObsoleteButton, PageHeader,
  SimpleSelect, StatusBadge, WorkflowBar, useQueryId,
} from "@/components/design/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, X } from "lucide-react";
import { toast } from "sonner";

const CFG = {
  documents: {
    title: "Drawings & Documents", subtitle: "CAD / PDF drawings and documents with revisions, controlled status and stamped downloads",
    newText: "New drawing / document", perm: "create_edit",
    empty: { title: "", doc_type: "drawing", description: "", item_nos: [], sheet_size: "", scale: "", customer_doc_no: "", standard_refs: [], number: "" },
    editable: ["title", "doc_type", "description", "item_nos", "sheet_size", "scale", "customer_doc_no", "standard_refs"],
  },
  standards: {
    title: "Standards & Specifications", subtitle: "Controlled library of national, international, customer and internal standards",
    newText: "New standard", perm: "standards_manage",
    empty: { number: "", title: "", standard_type: "internal", issuing_body: "", edition: "", description: "", scope: "", item_nos: [] },
    editable: ["title", "standard_type", "issuing_body", "edition", "description", "scope", "item_nos"],
  },
};

function PartsField({ value, onChange, disabled }) {
  const [pick, setPick] = useState("");
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {(value || []).map((n) => (
          <span key={n} className="mono text-xs bg-slate-100 rounded px-2 py-0.5 flex items-center gap-1">
            {n}{!disabled && <button onClick={() => onChange(value.filter((x) => x !== n))}><X className="w-3 h-3" /></button>}
          </span>
        ))}
        {!value?.length && <span className="text-xs text-slate-400">No parts linked</span>}
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <div className="flex-1"><ItemPicker value={pick} onChange={setPick} placeholder="Add part number" /></div>
          <Button type="button" variant="outline" size="sm" disabled={!pick}
            onClick={() => { if (!value.includes(pick)) onChange([...(value || []), pick]); setPick(""); }}>Link</Button>
        </div>
      )}
    </div>
  );
}

function DocForm({ kind, form, setForm, disabled, isNew, me }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  if (kind === "standards") {
    return (
      <div className="grid sm:grid-cols-2 gap-3">
        {isNew && <Field label="Standard no. * (e.g. IS 2062, ISO 9001)"><Input value={form.number} onChange={set("number")} data-testid="std-number" /></Field>}
        <Field label="Type"><SimpleSelect value={form.standard_type} onChange={set("standard_type")} disabled={disabled} options={me?.enums?.standard_types || []} /></Field>
        <Field label="Title *" className="sm:col-span-2"><Input value={form.title} onChange={set("title")} disabled={disabled} data-testid="doc-title" /></Field>
        <Field label="Issuing body"><Input value={form.issuing_body} onChange={set("issuing_body")} disabled={disabled} /></Field>
        <Field label="Edition / year"><Input value={form.edition} onChange={set("edition")} disabled={disabled} /></Field>
        <Field label="Scope" className="sm:col-span-2"><Textarea rows={2} value={form.scope} onChange={set("scope")} disabled={disabled} /></Field>
        <Field label="Description" className="sm:col-span-2"><Textarea rows={2} value={form.description} onChange={set("description")} disabled={disabled} /></Field>
        <Field label="Applies to parts" className="sm:col-span-2"><PartsField value={form.item_nos} onChange={set("item_nos")} disabled={disabled} /></Field>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Title *" className="sm:col-span-2"><Input value={form.title} onChange={set("title")} disabled={disabled} data-testid="doc-title" /></Field>
      <Field label="Type"><SimpleSelect value={form.doc_type} onChange={set("doc_type")} disabled={disabled} options={me?.enums?.document_types || []} /></Field>
      {isNew && <Field label="Document no. (blank = auto)"><Input value={form.number} onChange={set("number")} placeholder="Auto-generated" /></Field>}
      <Field label="Sheet size"><Input value={form.sheet_size} onChange={set("sheet_size")} disabled={disabled} placeholder="A3" /></Field>
      <Field label="Scale"><Input value={form.scale} onChange={set("scale")} disabled={disabled} placeholder="1:2" /></Field>
      <Field label="Customer document no."><Input value={form.customer_doc_no} onChange={set("customer_doc_no")} disabled={disabled} /></Field>
      <Field label="Standards referenced (comma separated)">
        <Input value={(form.standard_refs || []).join(", ")} disabled={disabled}
          onChange={(e) => setForm((f) => ({ ...f, standard_refs: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))} />
      </Field>
      <Field label="Description" className="sm:col-span-2"><Textarea rows={2} value={form.description} onChange={set("description")} disabled={disabled} /></Field>
      <Field label="Linked parts" className="sm:col-span-2"><PartsField value={form.item_nos} onChange={set("item_nos")} disabled={disabled} /></Field>
    </div>
  );
}

function DocDetail({ kind, id, onChanged, onOpen }) {
  const { me, can } = useDesignMe();
  const [rec, setRec] = useState(null);
  const [form, setForm] = useState(CFG[kind].empty);
  const [errors, setErrors] = useState([]);
  const load = useCallback(() => {
    api.get(`${D}/${kind}/${id}`).then((r) => { setRec(r.data); setForm({ ...CFG[kind].empty, ...r.data }); }).catch((e) => toast.error(designError(e)));
  }, [kind, id]);
  useEffect(load, [load]);
  if (!rec) return null;
  const editable = rec.status === "draft" && can(CFG[kind].perm);
  const save = async () => {
    setErrors([]);
    const body = {};
    CFG[kind].editable.forEach((k) => { body[k] = form[k]; });
    try {
      await api.patch(`${D}/${kind}/${id}`, body);
      toast.success("Saved");
      load();
      onChanged?.();
    } catch (e) {
      setErrors([designError(e)]);
    }
  };
  const changed = () => { load(); onChanged?.(); };
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-slate-500 mono">{rec.number} · Rev {rec.revision} · {label(rec.doc_type || rec.standard_type)}</div>
        <div className="text-xl font-bold text-slate-900">{rec.title}</div>
      </div>
      <WorkflowBar entity={kind} record={rec} onChanged={(n) => (n?.id && n.id !== rec.id ? onOpen(n.id) : changed())}
        extra={<ObsoleteButton entity={kind} record={rec} onChanged={changed} />} />
      <Tabs defaultValue="files">
        <TabsList>
          <TabsTrigger value="files">Files ({rec.files?.length || 0})</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="history">Revisions & History</TabsTrigger>
        </TabsList>
        <TabsContent value="files">
          <FilesPanel ownerType={kind} ownerId={rec.id} files={rec.files} editable={editable} onChanged={changed} title="Drawing files (CAD / PDF)" />
        </TabsContent>
        <TabsContent value="details" className="space-y-3">
          <DocForm kind={kind} form={form} setForm={setForm} disabled={!editable} me={me} />
          <ErrorList errors={errors} />
          {editable && <div className="flex justify-end"><Button className="bg-blue-600 hover:bg-blue-700" onClick={save}>Save draft</Button></div>}
        </TabsContent>
        <TabsContent value="history"><HistoryPanel entity={kind} record={rec} onOpenRevision={onOpen} /></TabsContent>
      </Tabs>
    </div>
  );
}

export default function DesignDocuments({ kind = "documents" }) {
  const cfg = CFG[kind];
  const { me, can, error: meErr } = useDesignMe();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [openId, setOpenId] = useState(useQueryId());
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(cfg.empty);
  const [errors, setErrors] = useState([]);
  const load = useCallback(() => {
    api.get(`${D}/${kind}`, { params: { q, status } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [kind, q, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("new")) { setCreating(true); if (p.get("item")) setForm((f) => ({ ...f, item_nos: [p.get("item")] })); }
  }, []);
  if (meErr || (me && !me.has_access)) return <NoAccess error={meErr} />;
  const create = async () => {
    setErrors([]);
    try {
      const body = { ...form };
      if (kind === "documents" && !body.number) delete body.number;
      const { data } = await api.post(`${D}/${kind}`, body);
      toast.success(`Created ${data.number} — attach the files, then submit for review`);
      setCreating(false);
      setForm(cfg.empty);
      load();
      setOpenId(data.id);
    } catch (e) {
      setErrors([designError(e)]);
    }
  };
  return (
    <div className="space-y-6 max-md:space-y-3" data-testid={`design-${kind}-page`}>
      <PageHeader title={cfg.title} subtitle={cfg.subtitle}>
        {can(cfg.perm) && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreating(true)} data-testid={`design-new-${kind}`}><Plus className="w-4 h-4 mr-1.5" />{cfg.newText}</Button>}
      </PageHeader>
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative w-72 max-md:w-full">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input className="pl-9" placeholder="Number, title…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="w-44 max-md:flex-1 max-md:min-w-[8rem]"><SimpleSelect value={status || "all"} onChange={(v) => setStatus(v === "all" ? "" : v)}
          options={[{ value: "all", label: "All statuses" }, ...(me?.enums?.statuses || []).map((s) => ({ value: s, label: label(s) }))]} /></div>
      </div>
      <DataTable rows={rows} onRowClick={(r) => setOpenId(r.id)} testid={`design-${kind}-table`}
        columns={[
          { key: "number", title: "Number", cellClass: "mono font-semibold" },
          { key: "revision", title: "Rev", cellClass: "mono" },
          { key: "title", title: "Title", cellClass: "max-w-sm truncate" },
          { key: "type", title: "Type", render: (r) => label(r.doc_type || r.standard_type) },
          { key: "item_nos", title: "Parts", render: (r) => (r.item_nos || []).slice(0, 3).join(", ") + ((r.item_nos || []).length > 3 ? "…" : "") },
          ...(kind === "documents" ? [{ key: "file_count", title: "Files", cellClass: "mono" }] : []),
          { key: "status", title: "Status", render: (r) => <StatusBadge status={r.status} stage={r.review_stage} /> },
          { key: "updated_at", title: "Updated", render: (r) => fmtDate(r.updated_at) },
        ]} />
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{cfg.newText}</DialogTitle></DialogHeader>
          <DocForm kind={kind} form={form} setForm={setForm} isNew me={me} />
          <ErrorList errors={errors} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={create} disabled={!form.title || (kind === "standards" && !form.number)} data-testid="doc-create">Create draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto">
          <SheetHeader><SheetTitle>{kind === "documents" ? "Drawing / Document" : "Standard"}</SheetTitle></SheetHeader>
          {openId && <DocDetail key={openId} kind={kind} id={openId} onChanged={load} onOpen={setOpenId} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
